import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// ─────────────────────────────────────────────────────────────
// Fase 13 — Interceptor 401 con auto-refresh silencioso.
//
// Cambios respecto a la versión anterior:
//   1. `/auth/me` YA NO está en `isAuthRoute` — un 401 en /auth/me
//      dispara refresh + retry, en vez de mandar al usuario a login.
//      Root cause del bug de "sesión de 30 min": al reabrir la PWA con
//      access-token expirado, AuthContext llamaba /auth/me → 401 → no
//      refresh → login page, aunque el refresh_token siguiera vivo.
//
//   2. Mutex real (`refreshPromise`) — múltiples requests que 401 en
//      paralelo comparten la MISMA promesa de refresh en vez de disparar
//      N refreshes concurrentes. Crítico cuando la PWA rehidrata y
//      lanza `/auth/me` + `/conversations` + `/notifications` a la vez.
// ─────────────────────────────────────────────────────────────

let refreshPromise = null;

async function _sharedRefresh() {
    if (!refreshPromise) {
        refreshPromise = api.post("/auth/refresh")
            .finally(() => { refreshPromise = null; });
    }
    return refreshPromise;
}

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error.config;
        const url = original?.url || "";
        // NOTA: /auth/me se REMUEVE deliberadamente de esta lista.
        const isAuthRoute = url.endsWith("/auth/refresh")
                         || url.endsWith("/auth/login");

        if (error.response?.status === 401 && !original._retried && !isAuthRoute) {
            original._retried = true;
            try {
                await _sharedRefresh();
            } catch (_) {
                // Refresh murió — sesión realmente muerta. Al login.
                if (typeof window !== "undefined"
                    && !window.location.pathname.startsWith("/login")) {
                    window.location.replace("/login");
                }
                return Promise.reject(error);
            }
            return api(original);
        }
        return Promise.reject(error);
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Algo ha fallado. Inténtalo de nuevo.";
    if (typeof detail === "string") {
        // Translate generic FastAPI auth errors so users don't see raw English copy
        if (detail === "Not authenticated") return "Sesión caducada. Vuelve a entrar.";
        if (detail === "Token expired")     return "Sesión caducada. Vuelve a entrar.";
        return detail;
    }
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const WS_URL = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws`;
